import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { lazy, type PropsWithChildren, Suspense, useEffect } from "react"
import { Provider as ReduxProvider } from "react-redux"

import { skipRefetchOnFocusAfter403, skipRetryOn4xx } from "@/common/api/retryPredicates"
import { installSessionSignal } from "@/common/auth/session-signal"
import SplashScreen from "@/common/components/Splashscreen"
import { DevModeProvider } from "@/common/contexts/DevModeContext"
import { ShowResourceIdsProvider } from "@/common/contexts/ShowResourceIdsContext"
import { DebugModeProvider } from "@/common/debug/DebugModeProvider"
import { isEmbedPage } from "@/common/routing/RouteProtection"
import { reportError } from "@/common/utils/error-handler"
import { DocumentCacheProvider } from "@/contexts/DocumentCacheContext"
import { store as reduxStore } from "@/store"

/**
 * The Clerk half of this tree, behind a real code-split boundary. Its own file
 * header carries the finding; what belongs here is the WARM-UP below.
 *
 * `isEmbedPage` is imported from `common/routing/RouteProtection` directly and
 * not from the `common/app` barrel, which re-exports this module — the barrel
 * would close a cycle for one pure pathname test.
 */
const importClerkAuthProviders = () => import("@/common/providers/ClerkAuthProviders")
const ClerkAuthProviders = lazy(importClerkAuthProviders)

/**
 * Start the console's Clerk chunk in the same tick this module evaluates.
 *
 * ⚠️ WITHOUT THIS THE SPLIT MOVES THE COST INSTEAD OF REMOVING IT. `clerk` was
 * in `index.html`'s modulepreload set, so the browser fetched it in parallel
 * with the entry; a bare `lazy()` turns that into a request the browser cannot
 * discover until the entry has evaluated AND React has rendered — one extra
 * round trip on the console's critical path, on the one provider every
 * authenticated screen waits for.
 *
 * The route is knowable from the URL before React renders, so the fetch starts
 * here rather than at first render. On `/embed/:embed-id` it never starts at
 * all, which is the whole point.
 */
if (typeof window !== "undefined" && !isEmbedPage(window.location.pathname)) {
  void importClerkAuthProviders()
}

/** Current route, used to attribute auto-reported errors (client-only). */
function currentRoute(): string | undefined {
  return typeof window === "undefined" ? undefined : window.location.pathname
}

/**
 * Stringifies a React Query key into a readable Sentry operation label.
 *
 * Only the first three segments, because every generated key is
 * `["api", <domain>, <operation>, roleScopeId, opts]` and the two tail segments are
 * per-CALL, not per-endpoint. Taking the whole key put `roleScopeId` — a real id
 * whenever the user is inside a role scope — into `operation`, and `operation` is
 * part of the Sentry fingerprint. One failing endpoint therefore split into one
 * issue per role scope, each too small to notice.
 *
 * Three is the endpoint's identity and nothing else. `agentVersions.list.infinite`
 * is the one generated key with four leading strings; it groups under
 * `api/agentVersions/list` alongside its non-infinite twin, which is correct — the
 * endpoint is what failed.
 */
function keyLabel(key: readonly unknown[]): string {
  return key
    .slice(0, 3)
    .filter((part) => typeof part === "string" || typeof part === "number")
    .join("/")
}

/**
 * Global error net: every query/mutation failure is reported to Sentry exactly once,
 * with zero per-call code. `reportError` dedupes (shared WeakSet) and skips expected
 * kinds, so a component `catch` calling `handleError` adds only UX, never a second
 * Sentry event. This is the automatic half of the centralized error system.
 */
const queryCache = new QueryCache({
  onError: (error, query) =>
    reportError(error, {
      source: "query",
      operation: keyLabel(query.queryKey),
      extra: { route: currentRoute() }
    })
})

const mutationCache = new MutationCache({
  onError: (error, _vars, _ctx, mutation) =>
    reportError(error, {
      source: "mutation",
      operation: mutation.options.mutationKey ? keyLabel(mutation.options.mutationKey) : "unknown",
      extra: { route: currentRoute() }
    })
})

// Client-level retry policy: skip 4xx, retain the default 3-attempt
// loop for 5xx and non-HTTP errors. Applied here so every `useGet`
// inherits the predicate for free — deleted-resource 404s, permission
// 403s, and malformed-param 400s no longer multiply into three
// console errors + three Sentry events per stale interaction.
//
// `refetchOnWindowFocus` is the SECOND multiplier over the same errors, and
// closing the first one reads as if it had closed both: `retry` governs one
// fetch, the focus refetch re-issues the request from scratch every time the
// tab is returned to. A 403 cannot become a 200 by waiting, so it is the one
// status that is withheld — see `skipRefetchOnFocusAfter403` for why the other
// 4xx codes deliberately keep refetching.
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: { retry: skipRetryOn4xx, refetchOnWindowFocus: skipRefetchOnFocusAfter403 }
  }
})

// `ApiAuthContext` now lives in its own leaf module so the API-client build
// chunk can read it without importing this provider tree. Re-exported here for
// the existing `@/common/providers/AppProviders` import sites.
export { ApiAuthContext } from "@/common/api/api-auth-context"

/**
 * Declares the embed surface anonymous. Embeds run with no ClerkProvider at all, so
 * without this the probe would sit at `unknown` forever and every embed 401 would be
 * reported as if it were an authenticated caller being refused.
 */
function AnonymousSessionBridge({ children }: PropsWithChildren) {
  useEffect(() => installSessionSignal(() => "anonymous"), [])
  return <>{children}</>
}

interface AppProvidersProps {
  pageProps: Record<string, unknown>
  /** Skip ClerkProvider for pages that don't need auth (e.g. embed pages) */
  isEmbed?: boolean
}

export function AppProviders({
  children,
  pageProps,
  isEmbed = false
}: Readonly<PropsWithChildren<AppProvidersProps>>) {
  const inner = (
    <ReduxProvider store={reduxStore}>
      <QueryClientProvider client={queryClient}>
        <DevModeProvider>
          <ShowResourceIdsProvider>
            <DebugModeProvider>
              <DocumentCacheProvider>{children}</DocumentCacheProvider>
            </DebugModeProvider>
          </ShowResourceIdsProvider>
        </DevModeProvider>
      </QueryClientProvider>
    </ReduxProvider>
  )

  // Embed pages are anonymous — ClerkProvider must be excluded because:
  // 1. Clerk accesses localStorage on init, which throws SecurityError in cross-origin iframes
  // 2. The embed chat has no authenticated user context
  // ApiAuthContext defaults to null token so API hooks work without crashing.
  if (isEmbed) return <AnonymousSessionBridge>{inner}</AnonymousSessionBridge>

  // `SplashScreen turnOff={false}` is the same fallback `main.tsx` gives `Boot`,
  // which is the very next thing this tree renders — so a console load that does
  // wait on this chunk shows the screen it was going to show anyway, not a blank
  // frame. The warm-up at module scope is what keeps that wait near zero.
  return (
    <Suspense fallback={<SplashScreen turnOff={false} />}>
      <ClerkAuthProviders pageProps={pageProps}>{inner}</ClerkAuthProviders>
    </Suspense>
  )
}
